import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowIcon, Footer, SiteHeader, Spinner } from '../components/SiteShell'
import { getSampleCharacters, getStoreBook, sampleUrl, studioUrl } from '../data/books'
import { bookService, type CatalogSampleCharacterId, type CatalogSampleResult } from '../services/book'

const CHARACTERS: Array<{ id: CatalogSampleCharacterId; name: string; description: string }> = [
  { id: 'lily', name: 'Lily', description: 'Girl sample' },
  { id: 'oliver', name: 'Oliver', description: 'Boy sample' },
]

function requestedCharacter(bookId: string): CatalogSampleCharacterId {
  const requested = new URLSearchParams(window.location.search).get('character')
  const available = getSampleCharacters(bookId)
  return available.find(character => character === requested) ?? available[0]
}

export default function SampleBookPage() {
  const params = useMemo(() => new URLSearchParams(window.location.search), [])
  const bookId = params.get('book') || ''
  const book = getStoreBook(bookId)
  const [characterId, setCharacterId] = useState<CatalogSampleCharacterId>(() => requestedCharacter(bookId))
  const [sample, setSample] = useState<CatalogSampleResult>()
  const [error, setError] = useState('')
  const [imageError, setImageError] = useState(false)
  const [requestVersion, setRequestVersion] = useState(0)
  const forceRefresh = useRef(false)

  const loadSample = useCallback(() => {
    forceRefresh.current = true
    setRequestVersion(version => version + 1)
  }, [])

  useEffect(() => {
    if (!book?.sampleAvailable) return
    let active = true
    setSample(undefined)
    setError('')
    setImageError(false)
    const refreshUrls = forceRefresh.current
    forceRefresh.current = false
    bookService.getCatalogSample(book.id, characterId, { forceRefresh: refreshUrls })
      .then(result => active && setSample(result))
      .catch(() => active && setError('We could not open this sample book. Please try again.'))
    return () => { active = false }
  }, [book, characterId, requestVersion])

  useEffect(() => {
    document.title = book ? `${book.title} sample | Lucie's Book` : `Sample book | Lucie's Book`
  }, [book])

  function chooseCharacter(nextCharacter: CatalogSampleCharacterId) {
    if (nextCharacter === characterId) return
    setCharacterId(nextCharacter)
    window.history.pushState({}, '', sampleUrl(bookId, nextCharacter))
  }

  if (!book || !book.sampleAvailable) {
    return (
      <>
        <SiteHeader simple onBack={() => { window.location.href = '/books/' }} />
        <main className="sample-page sample-state-page shell">
          <h1>This sample isn’t on the shelf yet.</h1>
          <p>Choose another book from the collection, or start creating your own.</p>
          <a className="button" href="/books/">Browse all books <ArrowIcon /></a>
        </main>
        <Footer />
      </>
    )
  }

  const availableCharacters = CHARACTERS.filter(character => getSampleCharacters(bookId).includes(character.id))
  const selectedCharacter = CHARACTERS.find(character => character.id === characterId)!

  return (
    <>
      <SiteHeader simple onBack={() => { window.location.href = '/books/' }} />
      <main className="sample-page">
        <section className="sample-hero shell">
          <div className={`sample-cover-stage sample-accent-${book.accent}`}>
            {sample
              ? <img src={sample.coverUrl} alt={`${book.title}, personalized for ${sample.character.name}`} onError={() => setImageError(true)} />
              : <div className="sample-cover-loading"><Spinner label="Loading sample cover" /><span>Opening the book…</span></div>}
          </div>
          <div className="sample-intro">
            <a className="sample-back-link" href="/books/"><ArrowIcon direction="left" /> All books</a>
            <h1>{book.title}</h1>
            <p>Read every page before you create your own. {availableCharacters.length > 1 ? 'Choose Lily or Oliver to see how one photo becomes a book made around a child.' : `Read with ${selectedCharacter.name} to see how one photo becomes a book made around a child.`}</p>
            {availableCharacters.length > 1 && <div className="sample-character-picker" role="group" aria-label="Choose a sample child">
              {availableCharacters.map(character => (
                <button key={character.id} type="button" className={character.id === characterId ? 'active' : ''} aria-pressed={character.id === characterId} onClick={() => chooseCharacter(character.id)}>
                  <strong>Read with {character.name}</strong>
                  <span>{character.description}</span>
                </button>
              ))}
            </div>}
            <a className="button sample-personalize" href={studioUrl(book.id)}>Create your own {book.title.replace(/ \(.+\)$/, '')} <ArrowIcon /></a>
            <small>Free personalized preview · Pay only when you order</small>
          </div>
        </section>

        <section className="sample-story shell" aria-labelledby="sample-story-title" aria-busy={!sample && !error}>
          <div className="sample-story-heading">
            <div><h2 id="sample-story-title">The whole story</h2><p>Cover to final page, starring {selectedCharacter.name}.</p></div>
            <span>26 pages</span>
          </div>

          {error && <div className="sample-message" role="alert"><strong>The book didn’t open.</strong><span>{error}</span><button type="button" onClick={loadSample}>Try again</button></div>}
          {imageError && !error && <div className="sample-message" role="alert"><strong>Some page links need refreshing.</strong><span>Get a fresh set of secure image links without losing your place.</span><button type="button" onClick={loadSample}>Refresh pages</button></div>}
          {!sample && !error && <div className="sample-gallery-loading"><Spinner label={`Loading ${selectedCharacter.name}'s sample book`} /><span>Preparing all 26 pages…</span></div>}
          {sample && (
            <div className="sample-page-grid">
              {sample.pages.map(page => (
                <figure className="sample-book-page" key={page.pageNumber}>
                  <img src={page.imageUrl} alt={page.title || `${book.title}, page ${page.pageNumber}`} loading={page.pageNumber <= 2 ? 'eager' : 'lazy'} decoding="async" onError={() => setImageError(true)} />
                  <figcaption><strong>{page.title || `Page ${page.pageNumber}`}</strong><span>{page.pageNumber} / 26</span></figcaption>
                </figure>
              ))}
            </div>
          )}
        </section>

        <section className="sample-final-cta">
          <div className="shell"><div><h2>Now make the story theirs.</h2><p>Use one favorite photo to create a free personalized preview.</p></div><a className="button button-light" href={studioUrl(book.id)}>Personalize this book <ArrowIcon /></a></div>
        </section>
      </main>
      <Footer />
    </>
  )
}
